import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { logEvent } from 'lib/analytics'
import { ROUTE_PATH } from 'lib/paths'
import { Link } from 'features/i18n/router'
import { CALL_TIMELINE_TYPE } from 'features/CallTimeline/CallTimeline'
import { useCallItem } from 'features/CallTimeline/components/CallList/CallList.hooks'
import { useGroupActions } from 'hooks'

import Avatar from 'components/Avatar'
import Nickname from 'components/Nickname'
import Button from 'components/Button'
import AvatarGroup from 'components/Avatar/AvatarGroup'
import LoadMore from 'components/LoadMore'

import { useCallTimeline } from './CallList.hooks'

import './style.scss'

export const CallItem = ({ call, attemptJoinGroup }) => {
  const { user, group, conference_call } = call
  const { t } = useTranslation()

  const { onCallClick } = useCallItem({ call, attemptJoinGroup })

  const totalPeopleInCall =
    conference_call.conference_call_users_count +
    conference_call.anonymous_call_users_count

  const getLabel = (totalPeopleInCall: number, isActive: boolean) => {
    if (totalPeopleInCall >= 18) {
      return t('common.call_full')
    } else {
      if (isActive) {
        return t('common.total_people_in_call', { total: totalPeopleInCall })
      } else {
        return t('common.total_people_was_in_call', {
          total: totalPeopleInCall,
        })
      }
    }
  }

  const isVideoCall = conference_call.call_type === 'vdo'

  return (
    <div className="CallItemList__item">
      {totalPeopleInCall > 1 ? (
        <AvatarGroup users={conference_call?.conference_call_users ?? []} />
      ) : (
        <Link
          onClick={() =>
            logEvent.user_profile_open({
              target_user_id: user?.id,
              from: group
                ? 'group_post_call_participant'
                : 'post_call_participant',
            })
          }
          to={ROUTE_PATH.USER(user?.id)}
        >
          <Avatar user={user} showVipCircle />
        </Link>
      )}

      <div className="CallItemList__item__detail">
        <Nickname user={user} />

        <div>
          {getLabel(totalPeopleInCall, conference_call.active)}
          {group && (
            <span className="CallItemList__item__detail__group_topic">
              ・{group?.topic}
            </span>
          )}
        </div>
      </div>

      <Button
        buttonStyle={isVideoCall ? 'primary' : 'secondary'}
        icon={isVideoCall ? 'video-call' : 'voice-call'}
        onClick={onCallClick}
      >
        {t('call_list_item.call_item.button')}
      </Button>
    </div>
  )
}

const CallItemList = ({ calls }: { calls: any }) => {
  const { t } = useTranslation()

  const { attemptJoinGroup, GroupActionConfirmBox } = useGroupActions()

  return (
    <Fragment>
      <div className="CallItemList">
        {!calls.isLoading && calls.isEmptyList && (
          <p className="CallItemList__empty">{t('call_list.not_found')}</p>
        )}

        {calls.combinedResults?.map((call: any) => (
          <CallItem
            key={call.id}
            call={call}
            attemptJoinGroup={attemptJoinGroup}
          />
        ))}

        <LoadMore
          isLoading={calls.isLoading}
          onClick={calls.onLoadMore}
          hidden={!calls.shouldLoadMore}
        />
      </div>

      <GroupActionConfirmBox />
    </Fragment>
  )
}

const OpenCallList = () => {
  const calls = useCallTimeline({ callTimeline: CALL_TIMELINE_TYPE.all })

  return <CallItemList calls={calls} />
}

const FollowingCallList = () => {
  const calls = useCallTimeline({ callTimeline: CALL_TIMELINE_TYPE.follower })

  return <CallItemList calls={calls} />
}

const CallList = (props: any) => {
  if (props?.callTimelineMode === 'all') return <OpenCallList />

  return <FollowingCallList />
}

export default CallList
